import React, { useContext, useState } from "react";
import { partition, startCase, toLower } from "lodash";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import { formatCoordinates } from "common/utils/coordinates";
import { OmniboxSearchResult } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { FlexRow } from "../Common/__styles__/Layout";
import { EmptyState } from "../Common/EmptyState";
import { IconNames } from "../Common/Icons/LucideIcons";
import { Tag } from "../Common/Tag";
import { Body } from "../Common/Typography";
import { SelectionCard } from "../Inputs/SelectionCard";
import { LayerContext } from "../Maps/layers";
import { SearchResultProps } from ".";

const formatSearchResultAddress = (address?: Maybe<string>) => {
  return startCase(toLower(address ?? ""));
};

const displayForResult = (
  result: OmniboxSearchResult,
): {
  title?: Maybe<string>;
  description?: Maybe<string>;
  iconName: IconNames;
  type: string;
  value: SearchResultProps;
} => {
  const matchReason =
    arrayHasAtLeastOneItem(result.matchReasons) ?
      result.matchReasons[0].value
    : undefined;
  if (result.__typename === "ParcelSearchResult") {
    return {
      title: result.parcelId,
      description: matchReason ?? formatSearchResultAddress(result.address),
      iconName: "land-plot",
      type: "Parcel",
      value: {
        point: {
          latitude: result.interiorPoint.coordinates[1],
          longitude: result.interiorPoint.coordinates[0],
        },
        address: result.address ?? "",
        geocacheParcelId: result.id,
        city: result.city,
        state: result.state,
        zip: result.zip,
      },
    };
  } else if (result.__typename === "PropertySearchResult") {
    return {
      title:
        result.streetAddress ?
          formatSearchResultAddress(result.streetAddress)
        : formatCoordinates({
            longitude: result.geometry.coordinates[0]!,
            latitude: result.geometry.coordinates[1]!,
          }),
      iconName: "home",
      type: "Property",
      description: matchReason,
      value: {
        point: {
          latitude: result.geometry.coordinates[1],
          longitude: result.geometry.coordinates[0],
        },
        address: result.streetAddress ?? "",
        propertyId: result.id,
        geocacheParcelId: result.geocacheParcelId,
        streetAddress: result.streetAddress,
        city: result.city,
        state: result.state,
        zip: result.zip,
      },
    };
  } else {
    throw new Error(`Unknown result type: ${result.__typename}`);
  }
};

const OmniboxResultItem = ({ result }: { result: OmniboxSearchResult }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { title, description, iconName, type, value } =
    displayForResult(result);

  return (
    <SelectionCard.Item
      key={result.id}
      size="compact"
      borderStyle="ghost"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      value={value}
    >
      <FlexRow style={{ gap: spacing.m.value }}>
        <SelectionCard.CommonContent
          iconName={iconName}
          title={title}
          description={description}
        />
        <div style={{ opacity: isHovered ? 1 : 0 }}>
          {/* TODO: check on tag height */}
          <Tag styleVariant="default">{type}</Tag>
        </div>
      </FlexRow>
    </SelectionCard.Item>
  );
};

export const OmniboxResults = ({
  handleSearchResultClick,
}: {
  handleSearchResultClick: (searchResult: SearchResultProps) => void;
}) => {
  const { searchState } = useContext(LayerContext);

  if (searchState.loading) {
    return <div>Loading...</div>;
  } else if (!searchState.results?.length) {
    return (
      <EmptyState
        icon="missing-location"
        paddingTop={spacing["2xl"].value}
        title="No results found"
        body="Check the spelling or search by street, city, or ZIP, then use the map to explore nearby."
      />
    );
  }

  const [strictMatches, fuzzyMatches] = partition(
    searchState.results,
    result => result.strictMatch,
  );

  return (
    <SelectionCard.Group
      direction="vertical"
      spacing="compact"
      handleSelection={handleSearchResultClick}
    >
      {strictMatches.map(result => (
        <OmniboxResultItem result={result} key={result.id} />
      ))}
      {fuzzyMatches.length > 0 && (
        <>
          <Body
            type="emphasis"
            size="small"
            style={{
              paddingTop: strictMatches.length > 0 ? spacing.m.value : 0,
            }}
          >
            Related Results
          </Body>
          {fuzzyMatches.map(result => (
            <OmniboxResultItem result={result} key={result.id} />
          ))}
        </>
      )}
    </SelectionCard.Group>
  );
};
